import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Check, X, ArrowsClockwise, MagnifyingGlass, Eye,
} from "@phosphor-icons/react";

// Human labels for the 12 permission keys
const PERM_LABELS = {
  can_edit_leads: { label: "Edit leads", group: "Leads", hint: "Update status, notes, name, phone" },
  can_reassign_leads: { label: "Reassign leads", group: "Leads", hint: "Move a lead to a different agent" },
  can_delete_leads: { label: "Delete leads", group: "Leads", hint: "Permanent — admins should keep this OFF for agents" },
  can_bulk_actions: { label: "Bulk actions", group: "Leads", hint: "Bulk reassign / remark / delete from the Leads page" },
  can_export: { label: "Export CSV", group: "Data", hint: "DATA-LEAK RISK — every export is watermarked" },
  can_import: { label: "Import CSV", group: "Data", hint: "Upload sheets / paste TSV" },
  can_view_full_phone: { label: "See full phone number", group: "Data", hint: "OFF = masked phone with click-to-reveal (every reveal is logged + rate-limited to 25/hr)" },
  can_view_reports: { label: "View Reports", group: "Pages", hint: "/reports analytics dashboard" },
  can_view_callyzer: { label: "View Callyzer", group: "Pages", hint: "Telephony call log" },
  can_view_kraya: { label: "View Kraya", group: "Pages", hint: "WhatsApp pipeline" },
  can_view_security: { label: "View Security", group: "Admin pages", hint: "Audit log + watermark decoder" },
  can_view_webhooks: { label: "View Webhooks", group: "Admin pages", hint: "Webhook URLs and secrets" },
  can_view_routing: { label: "View Routing", group: "Admin pages", hint: "Branch routing rules + backfill" },
};

const GROUPS = ["Leads", "Data", "Pages", "Admin pages"];

const SCOPE_LABEL = {
  own: { label: "Own leads", color: "#52525B", hint: "Only leads assigned to this user" },
  branch: { label: "Branch leads", color: "#002FA7", hint: "All leads in their branch" },
  all: { label: "All leads", color: "#FF9800", hint: "Org-wide visibility — admin equivalent for reads" },
};

const ROLE_DESC = {
  admin: "Full access — every toggle locked ON. Cannot be modified.",
  branch_manager: "Defaults: edits + bulk + export + import within their branch. No admin pages.",
  agent: "Defaults: edits own leads, views Reports/Callyzer/Kraya. No export/import/delete.",
};

export default function Permissions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("roles"); // "roles" | "users"
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [search, setSearch] = useState("");

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, u, b] = await Promise.all([
        api.get("/permissions/roles"),
        api.get("/permissions/users"),
        branches.length ? Promise.resolve({ data: branches }) : api.get("/branches"),
      ]);
      setRoles(r.data.roles);
      setUsers(u.data);
      setBranches(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const togglePermission = async (role, key, currentValue) => {
    if (role === "admin") return; // disallow client-side too
    const newValue = !currentValue;
    const saveKey = `${role}:${key}`;
    setSavingKey(saveKey);
    // Optimistic
    setRoles((prev) => prev.map((r) =>
      r.role === role ? { ...r, permissions: { ...r.permissions, [key]: newValue } } : r,
    ));
    try {
      await api.patch(`/permissions/roles/${role}`, { [key]: newValue });
      toast.success(`${PERM_LABELS[key].label} → ${role} ${newValue ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(formatApiError(e));
      // Revert
      setRoles((prev) => prev.map((r) =>
        r.role === role ? { ...r, permissions: { ...r.permissions, [key]: currentValue } } : r,
      ));
    }
    setSavingKey("");
  };

  const updateUser = async (userId, patch) => {
    const saveKey = `u:${userId}`;
    setSavingKey(saveKey);
    try {
      await api.patch(`/permissions/users/${userId}`, patch);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, ...patch } : u));
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
    setSavingKey("");
  };

  if (!isAdmin) {
    return <div className="p-8 text-sm text-muted-foreground">Admins only.</div>;
  }

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Access control"
        title="Permissions"
        description="Decide what every role can do — and override visibility per individual user."
        actions={
          <button onClick={load} data-testid="perms-refresh"
            className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
            <ArrowsClockwise size={16} /> Refresh
          </button>
        }
      />

      {/* Tabs */}
      <div className="px-8 pt-6 border-b border-border flex gap-1" data-testid="perms-tabs">
        <TabButton active={tab === "roles"} onClick={() => setTab("roles")} testid="tab-roles" icon={ShieldCheck}>
          Roles ({roles.length})
        </TabButton>
        <TabButton active={tab === "users"} onClick={() => setTab("users")} testid="tab-users" icon={Users}>
          Per-user overrides ({users.length})
        </TabButton>
      </div>

      {tab === "roles" ? (
        <div className="px-8 py-6 space-y-6" data-testid="roles-tab">
          {/* Role headers explainer */}
          <div className="grid md:grid-cols-3 gap-3">
            {roles.map((r) => (
              <div key={r.role} className={`border p-4 ${r.is_admin_role ? "border-primary bg-secondary" : "border-border bg-white"}`}
                data-testid={`role-card-${r.role}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Role</div>
                <div className="font-display text-lg font-bold tracking-tight mt-1">
                  {r.role.replace("_", " ")}
                  {r.is_admin_role && <span className="ml-2 text-xs uppercase tracking-[0.15em] font-bold text-primary">locked</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{ROLE_DESC[r.role]}</div>
              </div>
            ))}
          </div>

          {/* The grid */}
          {loading ? (
            <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>
          ) : (
            <div className="bg-white border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="perms-grid">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                    <th className="px-4 py-3 font-bold w-[35%]">Permission</th>
                    {roles.map((r) => (
                      <th key={r.role} className="px-4 py-3 font-bold text-center w-[20%]">
                        {r.role.replace("_", " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {GROUPS.map((group) => (
                    <React.Fragment key={group}>
                      <tr className="bg-secondary/40">
                        <td colSpan={roles.length + 1} className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">
                          {group}
                        </td>
                      </tr>
                      {Object.entries(PERM_LABELS).filter(([, v]) => v.group === group).map(([key, meta]) => (
                        <tr key={key} className="border-b border-border last:border-0">
                          <td className="px-4 py-3">
                            <div className="font-medium">{meta.label}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{meta.hint}</div>
                          </td>
                          {roles.map((r) => {
                            const val = !!r.permissions[key];
                            const saving = savingKey === `${r.role}:${key}`;
                            const disabled = r.is_admin_role || saving;
                            return (
                              <td key={r.role} className="px-4 py-3 text-center">
                                <button
                                  disabled={disabled}
                                  onClick={() => togglePermission(r.role, key, val)}
                                  data-testid={`perm-${r.role}-${key}`}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.15em] border transition-colors ${
                                    val
                                      ? "bg-[#E7F4E7] border-[#008A00] text-[#008A00]"
                                      : "bg-white border-border text-muted-foreground hover:border-destructive hover:text-destructive"
                                  } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                  {val ? <Check size={12} weight="bold" /> : <X size={12} weight="bold" />}
                                  {val ? "On" : "Off"}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* USERS TAB */
        <div className="px-8 py-6" data-testid="users-tab">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-xs">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                placeholder="Search users"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="user-search"
                className="w-full text-sm border border-border pl-9 pr-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
              />
            </div>
            <div className="text-xs text-muted-foreground tabular-nums" data-testid="user-count">
              {filteredUsers.length} of {users.length}
            </div>
          </div>

          <div className="bg-white border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                  <th className="px-4 py-3 font-bold">Name</th>
                  <th className="px-4 py-3 font-bold">Email</th>
                  <th className="px-4 py-3 font-bold">Branch</th>
                  <th className="px-4 py-3 font-bold w-44">Role</th>
                  <th className="px-4 py-3 font-bold w-44">Visibility</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">No users match.</td></tr>
                ) : filteredUsers.map((u) => {
                  const saving = savingKey === `u:${u.id}`;
                  return (
                    <tr key={u.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-2.5 font-medium">{u.name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{u.email}</td>
                      <td className="px-4 py-2.5 text-xs">{branchMap[u.branch_id] || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-4 py-2.5">
                        <select
                          value={u.role}
                          disabled={saving}
                          onChange={(e) => updateUser(u.id, { role: e.target.value })}
                          data-testid={`user-role-${u.id}`}
                          className="text-xs border border-border px-2 py-1 rounded-sm bg-white w-full disabled:opacity-50"
                        >
                          <option value="agent">Agent</option>
                          <option value="branch_manager">Branch Manager</option>
                        </select>
                      </td>
                      <td className="px-4 py-2.5">
                        <select
                          value={u.visibility_scope}
                          disabled={saving}
                          onChange={(e) => updateUser(u.id, { visibility_scope: e.target.value })}
                          data-testid={`user-scope-${u.id}`}
                          className="text-xs border border-border px-2 py-1 rounded-sm bg-white w-full disabled:opacity-50"
                          style={{ color: SCOPE_LABEL[u.visibility_scope]?.color }}
                        >
                          <option value="own">Own leads only</option>
                          <option value="branch">All leads in their branch</option>
                          <option value="all">All leads (org-wide)</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs text-muted-foreground flex items-start gap-2 max-w-2xl" data-testid="perms-help">
            <Eye size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>Visibility</strong> controls what leads the user sees in lists, exports and analytics —
              independent of the action toggles on the Roles tab. A Branch Manager with <em>own-leads</em> scope
              effectively works as a privileged solo agent; an Agent with <em>branch</em> scope can see their
              team&apos;s pipeline without being able to reassign or delete.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-5 py-2.5 text-sm font-bold uppercase tracking-[0.15em] flex items-center gap-2 border-b-2 transition-colors ${
        active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"
      }`}>
      {Icon && <Icon size={14} weight="bold" />}
      {children}
    </button>
  );
}
